import React, { useCallback, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  LegendType,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  barGraphConfig,
  OVERALL_ANALYSIS_GRAPH_MAX_BARS,
} from "../../../constants/constants";
import {
  formatOverallAnalysisCellDisplay,
  getOverallAnalysisGraphBarLabel,
  getOverallAnalysisDynamicColumnKeys,
  getOverallAnalysisTooltipDynamicColumns,
  OVERALL_ANALYSIS_FIXED_TOOLTIP_COLUMNS,
} from "../../../utils/trendSidebarGraphUtils/overallAnalysisColumns";

type BarData = {
  [key: string]: string;
};

type Props = {
  barData: BarData[];
  expanded?: boolean;
  /** Material chips (e.g. Color); affects which dynamic column is used for bar labels. */
  selectedMaterials?: string[];
};

const formatCompactTick = (value: number): string => {
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    return `${millions % 1 === 0 ? millions : millions.toFixed(1)}M`;
  }
  if (value >= 1_000) {
    const thousands = value / 1_000;
    return `${thousands % 1 === 0 ? thousands : thousands.toFixed(1)}K`;
  }
  return `${value}`;
};

const OverallAnalysisGraph: React.FC<Props> = ({
  barData,
  expanded = false,
  selectedMaterials,
}) => {
  const resolvedSelectedMaterials = useMemo(() => {
    if (selectedMaterials !== undefined) return selectedMaterials;
    if (barData?.[0] != null && "Color" in barData[0]) return ["Color"];
    return undefined;
  }, [selectedMaterials, barData]);

  const displayBarData = useMemo(
    () =>
      barData?.length ? barData.slice(0, OVERALL_ANALYSIS_GRAPH_MAX_BARS) : [],
    [barData]
  );

  /** Dynamic column for bar labels (not shown in tooltip hover). */
  const barLabelDynamicColumns = useMemo(() => {
    const dynamicKeys = displayBarData[0]
      ? getOverallAnalysisDynamicColumnKeys(
          displayBarData[0] as Record<string, unknown>
        )
      : [];
    return getOverallAnalysisTooltipDynamicColumns(
      dynamicKeys,
      resolvedSelectedMaterials
    );
  }, [displayBarData, resolvedSelectedMaterials]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const TriangleBar = useCallback((props: any) => {
    const { fill, x, y, width, height } = props;
    const minHeight = 10;
    const adjustedHeight = Math.max(minHeight, height);
    const baseY = y + height;
    const topY = baseY - adjustedHeight;
    const path = `M${x},${y + height}C${x + width / 3},${y + height} ${x + width / 2},${y + height / 3}
      ${x + width / 2},
      ${topY},
      C${x + width / 2},${y + height / 3} ${x + (2 * width) / 3},${y + height}
      ${x + width}, ${y + height}Z`;
    return <path d={path} stroke="none" fill={fill} />;
  }, []);

  const renderTooltip = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: any) => {
      const { active, payload } = props;
      if (!active || !payload?.length) return null;
      const data = payload[0].payload as Record<string, unknown>;
      const row = (labelText: string, columnKey: string, value: unknown) => (
        <div key={columnKey} className="text-secondary text-xs leading-relaxed">
          <span className="font-semibold">{labelText}: </span>
          {formatOverallAnalysisCellDisplay(columnKey, value)}
        </div>
      );
      return (
        <div
          className="border-4 rounded bg-gray-400 py-3 px-3 max-h-80 overflow-y-auto min-w-[180px]"
          style={{ backgroundColor: "#d5dbd6" }}
        >
          {OVERALL_ANALYSIS_FIXED_TOOLTIP_COLUMNS.map(
            ({ headerLabel, formatKey }) =>
              row(headerLabel, formatKey, data[formatKey])
          )}
        </div>
      );
    },
    []
  );

  const legendPayload = useMemo(
    () =>
      barGraphConfig.map(config => ({
        value: config.impact,
        type: "square" as LegendType,
        color: config.color,
      })),
    []
  );

  const yAxisDomain = useMemo(() => {
    if (!displayBarData.length) return [0, 100];

    const trendScores = displayBarData
      .map(item => Number(item["Trend Score"]))
      .filter(score => !isNaN(score));
    if (trendScores.length === 0) return [0, 100];

    const minScore = Math.min(...trendScores);
    const maxScore = Math.max(...trendScores);

    return [Math.min(0, Math.floor(minScore)), Math.ceil(maxScore * 1.5)];
  }, [displayBarData]);

  const labelOffset = useMemo(() => {
    if (!displayBarData.length) return expanded ? 10 : 20;

    const trendScores = displayBarData
      .map(item => Number(item["Trend Score"]))
      .filter(score => !isNaN(score));
    if (trendScores.length === 0) return expanded ? 10 : 20;

    const maxScore = Math.max(...trendScores);
    const baseOffset = expanded ? 10 : 20;
    const magnitudeFactor = Math.log10(Math.max(maxScore, 1));
    const dynamicOffset = baseOffset - magnitudeFactor * 3;

    return Math.round(dynamicOffset);
  }, [displayBarData, expanded]);

  return (
    <div className={`font-poppins ${expanded ? "w-full" : "w-[1200px]"}`}>
      <ResponsiveContainer width="100%" height={expanded ? 500 : 280}>
        <BarChart
          data={displayBarData}
          margin={{
            top: expanded ? 40 : 20,
            bottom: expanded ? 55 : 45,
            left: expanded ? 50 : 20,
            right: expanded ? 50 : 30,
          }}
          barGap={expanded ? 2 : 1}
        >
          <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 2" />
          <XAxis
            dataKey="tNo"
            width={1}
            tick={{ fontSize: expanded ? 14 : 12 }}
            tickFormatter={(value, index) => (index % 2 === 0 ? value : "")}
            label={{
              value: "Trend Rank",
              position: "insideBottom",
              offset: expanded ? -20 : -10,
              fill: "#666666",
              fontSize: expanded ? 13 : 12,
              fontWeight: "bold",
            }}
            axisLine={{
              stroke: "#000000",
              strokeWidth: 1,
              shapeRendering: "crispEdges",
            }}
          />
          <YAxis
            tick={{ fontSize: expanded ? 14 : 12 }}
            tickFormatter={formatCompactTick}
            axisLine={{
              stroke: "#000000",
              strokeWidth: 1,
              shapeRendering: "crispEdges",
            }}
            domain={yAxisDomain}
            label={{
              value: "Popularity",
              angle: -90,
              position: "insideLeft",
              offset: labelOffset,
              dy: 30,
              dx: -4,
              fill: "#666666",
              fontSize: expanded ? 13 : 12,
              fontWeight: "bold",
            }}
          />

          <Tooltip content={renderTooltip} />
          {expanded && (
            <Legend
              payload={legendPayload}
              align="right"
              verticalAlign="top"
              wrapperStyle={{
                paddingBottom: "20px",
                fontSize: "14px",
                fontWeight: 600,
              }}
            />
          )}
          <Bar
            dataKey="Trend Score"
            fill="#8884d8"
            shape={<TriangleBar />}
            barSize={45}
            label={
              expanded
                ? props => {
                    const { x, y, index } = props;
                    const entry = displayBarData[index];
                    const displayValue =
                      entry && barLabelDynamicColumns.length
                        ? getOverallAnalysisGraphBarLabel(
                            entry as Record<string, unknown>,
                            barLabelDynamicColumns,
                            resolvedSelectedMaterials
                          )
                        : "";
                    const words = displayValue ? displayValue.split(" ") : [];
                    return (
                      <text
                        x={x + 20}
                        y={y - 10}
                        textAnchor="start"
                        fill="#666666"
                        fontSize={12}
                        transform={`rotate(-90, ${x + 20}, ${y - 10})`}
                      >
                        {words.map((word: string, i: number) => (
                          <tspan key={i} x={x + 20} dy={i === 0 ? 0 : 14}>
                            {word}
                          </tspan>
                        ))}
                      </text>
                    );
                  }
                : false
            }
          >
            {displayBarData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.graphColour} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default OverallAnalysisGraph;
